import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

/**
 * Create / copy / regenerate / revoke a public read-only trip share link.
 * Pass usePartnerAuth when opened from the partner console.
 */
const ShareReadOnlyLinkDialog = ({ open, onClose, tripId, usePartnerAuth = false }) => {
  const { t } = useTranslation();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const loadLink = useCallback(async () => {
    if (!tripId) return;
    const authConfig = usePartnerAuth ? { usePartnerAuth: true } : {};
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/trips/${tripId}/share-link`, authConfig);
      setLink(res.data?.link || null);
    } catch (err) {
      setError(err.response?.data?.error || t('tripShare.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [tripId, t, usePartnerAuth]);

  useEffect(() => {
    if (open) {
      setCopied(false);
      loadLink();
    }
  }, [open, loadLink]);

  const partnerAuthConfig = usePartnerAuth ? { usePartnerAuth: true } : {};

  const createLink = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/trips/${tripId}/share-link`, {}, partnerAuthConfig);
      setLink(res.data?.link || null);
    } catch (err) {
      setError(err.response?.data?.error || t('tripShare.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  const regenerateLink = async () => {
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      const res = await api.post(`/trips/${tripId}/share-link`, { regenerate: true }, partnerAuthConfig);
      setLink(res.data?.link || null);
    } catch (err) {
      setError(err.response?.data?.error || t('tripShare.regenerateFailed'));
    } finally {
      setBusy(false);
    }
  };

  const revokeLink = async () => {
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      await api.delete(`/trips/${tripId}/share-link`, partnerAuthConfig);
      setLink(null);
    } catch (err) {
      setError(err.response?.data?.error || t('tripShare.revokeFailed'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      setError(t('tripShare.copyFailed'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkIcon fontSize="small" />
        {t('tripShare.title')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('tripShare.description')}
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : link?.url ? (
          <Box>
            <TextField
              fullWidth
              value={link.url}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2 }}
            />
            {copied ? (
              <Alert severity="success" sx={{ mb: 2 }}>{t('tripShare.copied')}</Alert>
            ) : null}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={copyLink}
                disabled={busy}
              >
                {t('tripShare.copy')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={regenerateLink}
                disabled={busy}
              >
                {t('tripShare.regenerate')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={revokeLink}
                disabled={busy}
              >
                {t('tripShare.revoke')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('tripShare.noneYet')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={createLink}
              disabled={busy}
            >
              {busy ? t('common.loading') : t('tripShare.create')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareReadOnlyLinkDialog;
